import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Info } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuditHistoryTab = () => {
  const { t } = useLanguage();
  const [auditType, setAuditType] = useState("all");
  const [auditData, setAuditData] = useState(null);

  const fetchAuditHistory = async (type = "all") => {
    try {
      const res = await fetch(`${API_URL}/api/employer/audit?type=${type}`, { credentials: 'include' });
      if (res.ok) setAuditData(await res.json());
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchAuditHistory(auditType);
  }, [auditType]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("auditHistory") || "Audit History"}</CardTitle>
        <CardDescription className="flex items-start gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          {t("auditHistoryDesc") || "Cleared data is archived here for accountability and compliance. This maintains a record of all shift requests, time-off requests, late check-ins, and IP flags that have been cleared."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex gap-2 mb-4 flex-wrap">
            <Button variant={auditType === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setAuditType('all')}>{t("all") || "All"}</Button>
            <Button variant={auditType === 'shifts' ? 'default' : 'outline'} size="sm" onClick={() => setAuditType('shifts')}>{t("shifts") || "Shifts"}</Button>
            <Button variant={auditType === 'time_off' ? 'default' : 'outline'} size="sm" onClick={() => setAuditType('time_off')}>{t("timeOff") || "Time Off"}</Button>
            <Button variant={auditType === 'ip_flags' ? 'default' : 'outline'} size="sm" onClick={() => setAuditType('ip_flags')}>{t("ipFlags") || "IP Flags"}</Button>
            <Button variant={auditType === 'late_notifications' ? 'default' : 'outline'} size="sm" onClick={() => setAuditType('late_notifications')}>{t("lateAlerts") || "Late Alerts"}</Button>
          </div>
          
          {auditData && (
            <div className="space-y-6">
              {(auditType === 'all' || auditType === 'shifts') && auditData.shifts?.length > 0 && (
                <AuditSection title={`${t("clearedShiftRequests") || "Cleared Shift Requests"} (${auditData.shifts.length})`} items={auditData.shifts} type="shifts" t={t} />
              )}
              
              {(auditType === 'all' || auditType === 'time_off') && auditData.time_off?.length > 0 && (
                <AuditSection title={`${t("clearedTimeOffRequests") || "Cleared Time Off Requests"} (${auditData.time_off.length})`} items={auditData.time_off} type="time_off" t={t} />
              )}
              
              {(auditType === 'all' || auditType === 'ip_flags') && auditData.ip_flags?.length > 0 && (
                <AuditSection title={`${t("clearedIpFlags") || "Cleared IP Flags"} (${auditData.ip_flags.length})`} items={auditData.ip_flags} type="ip_flags" t={t} />
              )}
              
              {(auditType === 'all' || auditType === 'late_notifications') && auditData.late_notifications?.length > 0 && (
                <AuditSection title={`${t("clearedLateNotifications") || "Cleared Late Notifications"} (${auditData.late_notifications.length})`} items={auditData.late_notifications} type="late_notifications" t={t} />
              )}
              
              {!auditData.shifts?.length && !auditData.time_off?.length && !auditData.ip_flags?.length && !auditData.late_notifications?.length && (
                <div className="text-center py-8 text-muted-foreground">{t("noAuditHistory") || "No audit history found"}</div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const AuditSection = ({ title, items, type, t }) => {
  return (
    <div>
      <h3 className="font-semibold mb-2">{title}</h3>
      <div className="space-y-2 max-h-60 overflow-y-auto">
        {items.map((item, i) => (
          <AuditItem key={i} item={item} type={type} t={t} />
        ))}
      </div>
    </div>
  );
};

const AuditItem = ({ item, type, t }) => {
  if (type === 'shifts') {
    return (
      <div className="p-3 rounded bg-muted/50 text-sm">
        <div className="flex justify-between">
          <span className="font-medium">{item.employee_name}</span>
          <span className="text-muted-foreground">{item.date}</span>
        </div>
        <p className="text-muted-foreground">{item.start_time} - {item.end_time} • {t("cleared") || "Cleared"}: {item.cleared_at?.split('T')[0]}</p>
      </div>
    );
  }
  
  if (type === 'time_off') {
    return (
      <div className="p-3 rounded bg-muted/50 text-sm">
        <div className="flex justify-between">
          <span className="font-medium">{item.employee_name}</span>
          <span className="text-muted-foreground">{item.date}</span>
        </div>
        <p className="text-muted-foreground">{item.reason_type} • {t("cleared") || "Cleared"}: {item.cleared_at?.split('T')[0]}</p>
      </div>
    );
  }
  
  if (type === 'ip_flags') {
    return (
      <div className="p-3 rounded bg-muted/50 text-sm">
        <span className="font-mono">{item.device_ip}</span>
        <p className="text-muted-foreground">{t("cleared") || "Cleared"}: {item.cleared_at?.split('T')[0]}</p>
      </div>
    );
  }
  
  if (type === 'late_notifications') {
    return (
      <div className="p-3 rounded bg-muted/50 text-sm">
        <div className="flex justify-between">
          <span className="font-medium">{item.employee_name}</span>
          <span className="text-muted-foreground">{item.timestamp?.split('T')[0]}</span>
        </div>
        <p className="text-muted-foreground">{t("lateCheckIn") || "Late check-in"}{item.late_reason ? ` • ${item.late_reason}` : ''}</p>
        <p className="text-muted-foreground text-xs">{t("cleared") || "Cleared"}: {item.cleared_at?.split('T')[0]}</p>
      </div>
    );
  }
  
  return null;
};

export default AuditHistoryTab;
